import React, { useState, useEffect } from 'react';
import {
  Box,
  Drawer,
  Paper,
  Typography,
  IconButton,
  Tooltip,
  Tabs,
  Tab,
  useTheme,
  useMediaQuery
} from '@mui/material';
import {
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  Visibility as VisibilityIcon,
  Person as PersonIcon,
  LocationOn as LocationIcon,
  Block as GeoBarrierIcon
} from '@mui/icons-material';
import DriverTable from './DriverTable';
import { UserInfoResponse } from '../../models/userInfo';

// Fixed drawer width
const DRAWER_WIDTH = 380;

interface DriverDrawerProps {
  userInfo: UserInfoResponse | null;
  selectedDrivers: string[];
  visibleTouchPoints: string[];
  showGeoBarriers: boolean;
  searchTerm: string;
  sortConfig: {
    key: 'name' | 'wtCapKg' | 'maxTp' | 'maxRangeKm' | 'vehicleType';
    direction: 'asc' | 'desc';
  };
  onDriverSelect: (driverId: string) => void;
  onTouchPointVisibilityToggle: (driverId: string) => void;
  onGeoBarrierToggle: () => void;
  onSearchChange: (term: string) => void;
  onSortChange: (key: 'name' | 'wtCapKg' | 'maxTp' | 'maxRangeKm' | 'vehicleType', direction: 'asc' | 'desc') => void;
}

/**
 * Component that provides a drawer containing driver, touchpoint, and geo barrier information
 */
const DriverDrawer: React.FC<DriverDrawerProps> = ({
  userInfo,
  selectedDrivers,
  visibleTouchPoints,
  showGeoBarriers,
  searchTerm,
  sortConfig,
  onDriverSelect,
  onTouchPointVisibilityToggle,
  onGeoBarrierToggle,
  onSearchChange,
  onSortChange
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  // State for drawer open status
  const [open, setOpen] = useState<boolean>(!isMobile);
  
  // State for active tab
  const [activeTab, setActiveTab] = useState<number>(0);
  
  // Update drawer state on mobile/desktop change
  useEffect(() => {
    setOpen(!isMobile);
  }, [isMobile]);
  
  // Handle drawer toggle
  const handleDrawerToggle = () => {
    setOpen(!open);
  };
  
  // Handle tab change
  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };
  
  return (
    <>
      {/* Drawer toggle button - visible when drawer is closed */}
      {!open && (
        <IconButton
          onClick={handleDrawerToggle}
          sx={{
            position: 'absolute',
            top: '50%',
            left: 0,
            zIndex: 10,
            backgroundColor: theme.palette.background.paper,
            borderRadius: '0 4px 4px 0',
            boxShadow: 2,
            '&:hover': {
              backgroundColor: theme.palette.background.default,
            },
          }}
        >
          <ChevronRightIcon />
        </IconButton>
      )}
      
      {/* Driver Drawer */}
      <Drawer
        variant="persistent"
        anchor="left"
        open={open}
        sx={{
          width: open ? DRAWER_WIDTH : 0,
          flexShrink: 0,
          position: 'relative',
          zIndex: 10,
          '& .MuiDrawer-paper': {
            width: DRAWER_WIDTH,
            boxSizing: 'border-box',
            position: 'relative',
            borderRight: `1px solid ${theme.palette.divider}`,
            height: '100%',
            overflow: 'hidden',
          },
        }}
      >
        {/* Drawer header with close button */}
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'space-between',
          padding: theme.spacing(1, 2),
          borderBottom: `1px solid ${theme.palette.divider}`,
        }}>
          <Typography variant="h6" component="div">
            User Information
          </Typography>
          <IconButton onClick={handleDrawerToggle}>
            <ChevronLeftIcon />
          </IconButton>
        </Box>
        
        {/* Tabs for navigating between views */}
        <Tabs 
          value={activeTab} 
          onChange={handleTabChange}
          variant="fullWidth"
          sx={{ borderBottom: `1px solid ${theme.palette.divider}` }}
        >
          <Tab 
            label="Drivers" 
            icon={<PersonIcon />} 
            iconPosition="start" 
          />
          <Tab 
            label="Touch Points" 
            icon={<LocationIcon />} 
            iconPosition="start" 
            disabled={!userInfo?.pastTps.length}
          />
          <Tab 
            label="Geo Barriers" 
            icon={<GeoBarrierIcon />} 
            iconPosition="start" 
            disabled={!userInfo?.config.geoBarriers.length}
          />
        </Tabs>
        
        {/* Tab content area */}
        <Box sx={{ 
          flex: 1, 
          overflow: 'auto',
          padding: theme.spacing(2),
          height: 'calc(100% - 112px)' // Account for header and tabs
        }}>
          {/* Drivers Tab */}
          {activeTab === 0 && userInfo && (
            <DriverTable
              drivers={userInfo.drivers}
              selectedDrivers={selectedDrivers}
              onDriverSelect={onDriverSelect}
              searchTerm={searchTerm}
              onSearchChange={onSearchChange}
              sortConfig={sortConfig}
              onSortChange={onSortChange}
            />
          )}
          
          {/* Touch Points Tab */}
          {activeTab === 1 && userInfo && (
            <Box>
              <Typography variant="h6" gutterBottom>
                Historical Touch Points
              </Typography>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                These touch points represent previous delivery/pickup locations.
              </Typography>
              
              <Box sx={{ mt: 2 }}>
                {userInfo.pastTps.map((tp) => (
                  <Paper
                    key={tp.tpId}
                    elevation={1}
                    sx={{
                      p: 2,
                      mb: 2,
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                    }}
                  >
                    <Box>
                      <Typography variant="subtitle1">{tp.tpName}</Typography>
                      <Typography variant="body2" color="text.secondary">
                        {tp.tpAddress}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        ZIP: {tp.tpZipCode}
                      </Typography>
                    </Box>
                    <Tooltip title="Toggle visibility on map">
                      <IconButton
                        size="small"
                        onClick={() => {
                          // This assumes touchpoints are associated with drivers
                          // In a real app, you'd need a specific mapping
                          onTouchPointVisibilityToggle(tp.tpId);
                        }}
                        color={visibleTouchPoints.includes(tp.tpId) ? "primary" : "default"}
                      >
                        <VisibilityIcon />
                      </IconButton>
                    </Tooltip>
                  </Paper>
                ))}
              </Box>
            </Box>
          )}
          
          {/* Geo Barriers Tab */}
          {activeTab === 2 && userInfo && (
            <Box>
              <Typography variant="h6" gutterBottom>
                Geo Barriers
              </Typography>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                These barriers represent restricted areas for certain vehicle types.
              </Typography>
              
              <Box sx={{ mt: 2 }}>
                {userInfo.config.geoBarriers.map((barrier) => (
                  <Paper
                    key={barrier.id}
                    elevation={1}
                    sx={{
                      p: 2,
                      mb: 2,
                    }}
                  >
                    <Typography variant="subtitle1">{barrier.name}</Typography>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      Allowed Vehicle Types: {barrier.allowedVehTypes.join(', ') || 'None'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Boundary Points: {barrier.geoBoundary.length}
                    </Typography>
                  </Paper>
                ))}
              </Box>
              
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                <Tooltip title={`${showGeoBarriers ? 'Hide' : 'Show'} geo barriers on map`}>
                  <IconButton
                    onClick={onGeoBarrierToggle}
                    color={showGeoBarriers ? "primary" : "default"}
                  >
                    <VisibilityIcon />
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>
          )}
          
          {/* Display when no userInfo is available */}
          {!userInfo && (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
              <Typography variant="body1" color="text.secondary">
                No user information available
              </Typography>
            </Box>
          )}
        </Box>
      </Drawer>
    </>
  );
};

export default DriverDrawer; 
